import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Spin, Typography } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import message from "@/utils/globalMessage";
import { formatServiceRequestRef, formatWorkorderRef } from "@/utils/entity-ref";
import { api } from "@/redux/api";
import { buildFacilityPath, buildPortfolioPath } from "@/utils/route-context";
import ContractRequestForm from "./ContractRequestForm";
import { CC_EMAILS_MAX } from "./ContractCcEmailsSelect";
import {
  CreatePage,
  CreateHeader,
  HeaderIdentity,
  HeaderActions,
  FormPanel,
  FormCard,
} from "./ContractRequestCreate.styles";
import {
  CONTRACT_CREATE_PREFILL_KEY,
  clearFlowPrefill,
  readAndClearFlowPrefill,
} from "@/utils/flow-prefill";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeContractPrefill = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  const normalized = { ...payload };
  if (
    (normalized.service_request === undefined || normalized.service_request === null) &&
    normalized.service_request_id !== undefined &&
    normalized.service_request_id !== null
  ) {
    normalized.service_request = normalized.service_request_id;
  }
  // The contract form uses a multi-select, while work-order records expose a
  // single assigned contractor. Keep both entry points compatible.
  if (
    !Array.isArray(normalized.contractor_ids) &&
    normalized.contractor !== undefined &&
    normalized.contractor !== null &&
    normalized.contractor !== ""
  ) {
    const contractorId = Number(normalized.contractor);
    if (Number.isInteger(contractorId) && contractorId > 0) {
      normalized.contractor_ids = [contractorId];
    }
  }
  // equipment 已改多选：兼容旧的单值 prefill 写入方
  if (
    !Array.isArray(normalized.equipments) &&
    normalized.equipment !== undefined &&
    normalized.equipment !== null &&
    normalized.equipment !== ""
  ) {
    const equipmentId = Number(normalized.equipment);
    if (Number.isInteger(equipmentId) && equipmentId > 0) {
      normalized.equipments = [equipmentId];
    }
  }
  return normalized;
};

export default function ContractRequestCreatePage({ scope = "facility" }) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, facilityId } = useParams();
  const selectedFacility = useSelector((state) => state.Facility?.selected);
  const userFacilities = useSelector((state) => state.Auth?.user_facilities);
  const [facilities, setFacilities] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSource, setLoadingSource] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const facilityContext = useMemo(() => ({
    companyId,
    facilityId,
  }), [companyId, facilityId]);

  const listPath = useMemo(() => {
    if (scope === "portfolio") return buildPortfolioPath("contract-request", { companyId }) || "/switch-view";
    return buildFacilityPath("contract-request", facilityContext) || "/switch-view";
  }, [companyId, facilityContext, scope]);

  useEffect(() => {
    let active = true;
    const loadFacilities = async () => {
      try {
        let rows;
        if (scope === "facility") {
          const selectedId = selectedFacility?.Id ?? selectedFacility?.id;
          const selectedName = String(selectedId) === String(facilityId)
            ? selectedFacility?.name || selectedFacility?.Name
            : undefined;
          rows = facilityId
            ? [{
              id: Number(facilityId),
              name: selectedName || `Facility #${facilityId}`,
            }]
            : [];
        } else {
          rows = (Array.isArray(userFacilities) ? userFacilities : [])
            .filter((item) => String(item.company_id) === String(companyId))
            .map((item) => ({ id: item.id, name: item.name }));
        }

        if (active) setFacilities(rows);
      } catch {
        if (active) message.error("Failed to load facilities");
      }
    };

    loadFacilities().finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [companyId, facilityId, scope, selectedFacility, userFacilities]);

  useEffect(() => {
    const sessionPrefill = readAndClearFlowPrefill(CONTRACT_CREATE_PREFILL_KEY);
    if (sessionPrefill) {
      setInitialValues(normalizeContractPrefill(sessionPrefill));
      setLoadingSource(false);
      return undefined;
    }

    const params = new URLSearchParams(location.search);
    const source = params.get("source");
    const sourceId = Number(params.get("source_id"));
    if (!source || !sourceId) {
      setLoadingSource(false);
      return undefined;
    }

    let active = true;
    setLoadingSource(true);
    const loader = source === "workorder"
      ? api.getWorkorderById(sourceId)
      : api.getFacilityServiceRequest(sourceId);
    loader
      .then((response) => {
        if (!active) return;
        const record = response?.data || response;
        const sourceFacility = record.facility?.id || record.facility || facilityId;
        const sourceEquipments = Array.isArray(record.equipments) && record.equipments.length
          ? record.equipments
          : (record.equipment?.id || record.equipment ? [record.equipment?.id || record.equipment] : undefined);
        const sourceServiceRequest =
          record.service_request ||
          record.service_request_id ||
          record.service_request_summary?.id ||
          (source === "tenant-request" ? sourceId : undefined);
        const sourceContractor = record.contractor?.id || record.contractor || record.contractor_id;
        const sourceCategory =
          record.service_category?.id ||
          record.service_category ||
          record.category?.id ||
          record.category ||
          record.service_request_summary?.category;
        setInitialValues({
          source,
          source_id: sourceId,
          sourceLabel: source === "workorder"
            ? `Work Order ${formatWorkorderRef(record.workordernumber ?? sourceId)}`
            : `Tenant Request ${formatServiceRequestRef(sourceId)}`,
          workorder: source === "workorder" ? sourceId : undefined,
          service_request: sourceServiceRequest,
          service_request_id: sourceServiceRequest,
          facility: Number(sourceFacility),
          equipments: sourceEquipments,
          title: record.issue || record.title,
          description: record.description,
          priority: record.priority,
          due_date: record.due_date,
          estimate_budget:
            record.estimate_budget ??
            (record.cost_approval_status === "approved" ? record.quoted_cost : undefined),
          contractor: sourceContractor,
          contractor_ids: sourceContractor ? [Number(sourceContractor)] : undefined,
          service_category: sourceCategory,
        });
      })
      .catch(() => {
        if (active) message.error("Failed to load request details");
      })
      .finally(() => {
        if (active) setLoadingSource(false);
      });

    return () => {
      active = false;
    };
  }, [facilityId, location.search]);

  useEffect(() => () => clearFlowPrefill(CONTRACT_CREATE_PREFILL_KEY), []);

  const submit = async (values) => {
    // These addresses ride along as CC on the notification emails the creator
    // already receives, so normalise (lowercase + dedupe) and reject typos
    // before hitting the API.
    const ccEmails = Array.from(
      new Set(
        (values.cc_emails || [])
          .map((item) => String(item).trim().toLowerCase())
          .filter(Boolean)
      )
    );
    const invalidCcEmail = ccEmails.find((item) => !EMAIL_PATTERN.test(item));
    if (invalidCcEmail) {
      message.error(`Invalid email address: ${invalidCcEmail}`);
      return;
    }
    if (ccEmails.length > CC_EMAILS_MAX) {
      message.error(`You can add up to ${CC_EMAILS_MAX} CC email addresses`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        cc_emails: ccEmails,
        due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : null,
        bid_due_date: values.bid_due_date ? values.bid_due_date.format("YYYY-MM-DD") : null,
      };
      const response = await api.createContract(payload);
      clearFlowPrefill(CONTRACT_CREATE_PREFILL_KEY);
      message.success("Contract request created");
      const requestId = response?.data?.id;
      if (!requestId) {
        navigate(listPath, { replace: true });
        return;
      }
      const detailPath = scope === "portfolio"
        ? buildPortfolioPath(`contract-request-detail/${requestId}`, { companyId })
        : buildFacilityPath(`contract-request-detail/${requestId}`, facilityContext);
      navigate(detailPath || listPath, { replace: true });
    } catch (error) {
      const data = error?.response?.data;
      message.error(data?.detail || Object.values(data || {})[0] || "Failed to create contract request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    clearFlowPrefill(CONTRACT_CREATE_PREFILL_KEY);
    navigate(listPath);
  };

  const title = initialValues?.sourceLabel
    ? `Create Contract from ${initialValues.sourceLabel}`
    : "Create Contract Request";

  return (
    <CreatePage>
      <CreateHeader>
        <HeaderIdentity>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleCancel}
            title="Back"
            aria-label="Back"
          />
          <Typography.Title level={4}>{title}</Typography.Title>
        </HeaderIdentity>
        <HeaderActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={submitting}
            onClick={() => form.submit()}
          >
            Create Request
          </Button>
        </HeaderActions>
      </CreateHeader>

      <Spin spinning={loading || loadingSource}>
        <FormPanel>
          <FormCard>
            <ContractRequestForm
              form={form}
              facilityIds={facilities.map((item) => item.id)}
              facilities={facilities}
              initialValues={initialValues}
              onFinish={submit}
            />
          </FormCard>
        </FormPanel>
      </Spin>
    </CreatePage>
  );
}
